import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  CreditCard, Globe, Eye, Inbox, ArrowUp, ArrowRight,
  Plus, Upload, CheckCircle, XCircle, Cloud, AlertTriangle,
  Rocket, Edit2, MessageSquare, Archive, LayoutGrid, Database,
  Users, Zap, TrendingUp, Activity, Clock
} from 'lucide-react'
import { useQuery } from 'react-query'
import { apiService } from '../../services/api'
import { useAuth } from '../../context/AuthContext'

// ── SVG chart primitives (no recharts dependency) ───────────────────────────

function smoothPath(points) {
  if (points.length < 2) return ''
  let d = `M ${points[0][0]} ${points[0][1]}`
  for (let i = 1; i < points.length; i++) {
    const [x0, y0] = points[i - 1]
    const [x1, y1] = points[i]
    const cx = (x0 + x1) / 2
    d += ` C ${cx} ${y0}, ${cx} ${y1}, ${x1} ${y1}`
  }
  return d
}

function MiniAreaChart({ data, height = 44, color = 'var(--accent)', fillId = 'areaFill', strokeWidth = 1.5, padding = 4 }) {
  if (!data || data.length < 2) return null
  const w = 100
  const h = height
  const max = Math.max(...data)
  const min = Math.min(...data)
  const range = max - min || 1
  const points = data.map((v, i) => [
    padding + (i * (w - padding * 2)) / (data.length - 1),
    padding + (1 - (v - min) / range) * (h - padding * 2),
  ])
  const d = smoothPath(points)
  const area = `${d} L ${points[points.length - 1][0]} ${h - padding} L ${points[0][0]} ${h - padding} Z`
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ width: '100%', height }}>
      <defs>
        <linearGradient id={fillId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.32" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${fillId})`} />
      <path d={d} fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" vectorEffect="non-scaling-stroke" />
    </svg>
  )
}

function MultiAreaChart({ series, height = 220 }) {
  const w = 100
  const h = height
  const padding = 10
  const allMax = Math.max(...series.flatMap(s => s.data))
  const allMin = 0
  const range = allMax - allMin || 1
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ width: '100%', height }}>
      <defs>
        {series.map((s, i) => (
          <linearGradient key={i} id={`gradS${i}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={s.color} stopOpacity="0.28" />
            <stop offset="100%" stopColor={s.color} stopOpacity="0" />
          </linearGradient>
        ))}
      </defs>
      {[0.25, 0.5, 0.75].map((g, i) => (
        <line key={i} x1="0" x2={w}
          y1={padding + g * (h - padding * 2)}
          y2={padding + g * (h - padding * 2)}
          stroke="var(--border)" strokeDasharray="1.5 3" strokeWidth="0.4" />
      ))}
      {series.map((s, sIdx) => {
        const points = s.data.map((v, i) => [
          padding + (i * (w - padding * 2)) / (s.data.length - 1),
          padding + (1 - (v - allMin) / range) * (h - padding * 2),
        ])
        const d = smoothPath(points)
        const area = `${d} L ${points[points.length - 1][0]} ${h - padding} L ${points[0][0]} ${h - padding} Z`
        return (
          <g key={sIdx}>
            <path d={area} fill={`url(#gradS${sIdx})`} opacity="0.8" />
            <path d={d} fill="none" stroke={s.color} strokeWidth="1.4" vectorEffect="non-scaling-stroke" strokeLinecap="round" />
          </g>
        )
      })}
    </svg>
  )
}

function BarChart({ data, height = 70, color = 'rgba(124,92,255,0.45)' }) {
  if (!data || data.length === 0) return null
  const max = Math.max(...data)
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height, width: '100%' }}>
      {data.map((v, i) => (
        <div key={i} style={{
          flex: 1,
          height: `${(v / max) * 100}%`,
          background: i === data.length - 1 ? 'var(--accent)' : color,
          opacity: i === data.length - 1 ? 1 : 0.6,
          borderRadius: 2,
          transition: 'height 0.4s ease',
        }} />
      ))}
    </div>
  )
}

function DonutChart({ data, size = 120, thickness = 14, centerLabel, centerValue }) {
  const total = data.reduce((s, d) => s + d.value, 0)
  const r = (size - thickness) / 2
  const c = 2 * Math.PI * r
  let offset = 0
  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--border)" strokeWidth={thickness} />
        {data.map((d, i) => {
          const len = (d.value / total) * c
          const dasharray = `${len} ${c - len}`
          const seg = (
            <circle key={i} cx={size / 2} cy={size / 2} r={r}
              fill="none" stroke={d.color}
              strokeWidth={thickness}
              strokeDasharray={dasharray}
              strokeDashoffset={-offset}
              strokeLinecap="butt" />
          )
          offset += len + 2
          return seg
        })}
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontSize: 18, fontWeight: 600, letterSpacing: '-0.02em', fontFamily: 'var(--font-mono)' }}>{centerValue}</div>
        <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{centerLabel}</div>
      </div>
    </div>
  )
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const timeAgo = (dateStr) => {
  if (!dateStr) return ''
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  return `${Math.floor(hrs / 24)}d ago`
}

const PRIORITY_BADGE = {
  urgent: 'badge-danger',
  high: 'badge-danger',
  medium: 'badge-warning',
  low: 'badge-info',
  normal: 'badge-info',
}

const ACT_ICON = {
  publish: Rocket, site_published: Rocket,
  edit: Edit2,
  comment: MessageSquare, request_submitted: MessageSquare,
  billing: CreditCard,
  deploy: Cloud,
  request: Inbox,
  domain: Globe,
  backup: Archive,
  client_registered: Users,
  new_page: Plus,
  default: Edit2,
}
const ACT_TONE = {
  publish: 'success', site_published: 'success',
  edit: 'info', new_page: 'info',
  comment: 'warning', request_submitted: 'warning',
  billing: 'success', client_registered: 'success',
  deploy: 'info',
  request: 'warning',
  domain: 'info',
  backup: '',
}

function getActivityText(a) {
  const user = a.user_name || a.user || 'Someone'
  const site = a.site_name || a.site || 'a site'
  const client = a.company_name || a.client || ''
  if (a.type === 'site_published') return `${user} published ${site}${client ? ` for ${client}` : ''}`
  if (a.type === 'client_registered') return `New client ${client || user} registered`
  if (a.type === 'request_submitted') return `${user} submitted a request for ${site}`
  return `${user} ${a.description || a.action || 'made an edit'} on ${site}${client ? ` (${client})` : ''}`
}

// ── Sub-components ───────────────────────────────────────────────────────────

function StatCard({ label, value, sub, delta, deltaText, series, color, icon: IconComp, fillId }) {
  return (
    <div className="card card-pad card-glow" style={{ position: 'relative', overflow: 'hidden' }}>
      <div className="flex items-center justify-between" style={{ marginBottom: 14 }}>
        <span className="icon-tile" style={{ background: `${color}1F`, color, borderColor: 'transparent' }}>
          <IconComp size={15} strokeWidth={1.8} />
        </span>
        <span className={`badge ${delta === 'up' ? 'badge-success' : 'badge-danger'}`}>
          {delta === 'up'
            ? <ArrowUp size={10} />
            : <ArrowUp size={10} style={{ transform: 'rotate(180deg)' }} />}
          {deltaText}
        </span>
      </div>
      <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-muted)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 600, letterSpacing: '-0.025em', marginTop: 4, fontFamily: 'var(--font-mono)' }}>{value}</div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{sub}</div>
      {series && series.length > 1 && (
        <div style={{ margin: '10px -22px -22px', height: 44 }}>
          <MiniAreaChart data={series} height={44} color={color} fillId={fillId} strokeWidth={1.5} padding={4} />
        </div>
      )}
    </div>
  )
}

function ActivityRow({ a }) {
  const type = a.type || a.edit_type || 'default'
  const tone = ACT_TONE[type] || ''
  const IconComp = ACT_ICON[type] || ACT_ICON.default
  return (
    <div className="activity-row">
      <div className="activity-tail" />
      <span className={`icon-tile ${tone}`} style={{ width: 28, height: 28, fontSize: 12, position: 'relative', zIndex: 1, flexShrink: 0 }}>
        <IconComp size={12} strokeWidth={2} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12.5, color: 'var(--text-soft)' }}>
          {getActivityText(a)}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
          <Clock size={9} />
          {timeAgo(a.created_at || a.timestamp)}
        </div>
      </div>
    </div>
  )
}

function RequestRow({ req }) {
  const badgeClass = PRIORITY_BADGE[req.priority] || 'badge-info'
  return (
    <Link to={`/admin/requests?open=${req.id}`} style={{ textDecoration: 'none' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
        onMouseEnter={e => e.currentTarget.style.opacity = '0.8'}
        onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
        <div className={`ds-avatar ds-avatar-${(req.id?.charCodeAt(0) % 4) + 1}`} style={{ width: 28, height: 28, fontSize: 10 }}>
          {(req.company_name || req.title || '?').slice(0, 2).toUpperCase()}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12.5, color: 'var(--text)', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {req.title}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>
            {req.company_name} · {timeAgo(req.created_at)}
          </div>
        </div>
        <span className={`badge ${badgeClass}`} style={{ fontSize: 10 }}>{req.priority || 'normal'}</span>
      </div>
    </Link>
  )
}

function ClientRow({ client, plansMap }) {
  return (
    <Link to={`/admin/clients/${client.id}`} style={{ textDecoration: 'none' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', borderBottom: '1px solid var(--border)' }}
        onMouseEnter={e => e.currentTarget.style.opacity = '0.75'}
        onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
        <div className={`ds-avatar ds-avatar-${(client.id?.charCodeAt(0) % 4) + 1}`} style={{ width: 28, height: 28, fontSize: 10 }}>
          {(client.company_name || '?').slice(0, 2).toUpperCase()}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12.5, color: 'var(--text)', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {client.company_name}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>
            {client.site_count || 0} {client.site_count === 1 ? 'site' : 'sites'} · {timeAgo(client.created_at)}
          </div>
        </div>
        <span style={{ fontSize: 11, color: 'var(--text-muted)', flexShrink: 0 }}>
          {plansMap?.[client.subscription_plan]?.name || client.subscription_plan || 'Basic'}
        </span>
      </div>
    </Link>
  )
}

function LegendItem({ color, label, value, delta, up }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: 'var(--text-muted)' }}>
        <span style={{ width: 8, height: 8, borderRadius: 3, background: color, flexShrink: 0 }} />
        {label}
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
        <span style={{ fontSize: 16, fontWeight: 600, letterSpacing: '-0.02em', fontFamily: 'var(--font-mono)' }}>{value}</span>
        {delta && (
          <span style={{ fontSize: 11, color: up ? 'var(--success)' : 'var(--danger)', display: 'flex', alignItems: 'center', gap: 2 }}>
            {up ? <ArrowUp size={9} /> : <ArrowUp size={9} style={{ transform: 'rotate(180deg)' }} />}
            {delta}
          </span>
        )}
      </div>
    </div>
  )
}

// ── Main component ───────────────────────────────────────────────────────────

const PERIODS = ['Today', '30 days', '90 days', 'YTD']
const PERIOD_KEYS = { 'Today': '7d', '30 days': '30d', '90 days': '90d', 'YTD': '90d' }

const PLAN_COLORS = ['#7C5CFF', '#5B7CFF', '#2BD9A6', '#FFB454']

export default function AdminOverview() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [activePeriod, setActivePeriod] = useState('30 days')
  const selectedPeriod = PERIOD_KEYS[activePeriod]

  const greeting = (() => {
    const h = new Date().getHours()
    if (h < 12) return 'Good morning'
    if (h < 18) return 'Good afternoon'
    return 'Good evening'
  })()

  const { data: stats, isLoading: statsLoading } = useQuery(
    ['admin-stats', selectedPeriod],
    () => apiService.get(`/users/admin/dashboard-stats?period=${selectedPeriod}`).then(r => r.data),
    { staleTime: 300000, retry: 2 }
  )

  const { data: activities, isLoading: activitiesLoading } = useQuery(
    ['admin-activities', selectedPeriod],
    () => {
      const daysMap = { '7d': 7, '30d': 30, '90d': 90 }
      const days = daysMap[selectedPeriod] || 30
      const startDate = new Date()
      startDate.setDate(startDate.getDate() - days)
      return apiService.get(`/edit-logs?limit=7&startDate=${startDate.toISOString()}`).then(r => r.data.logs || [])
    },
    { staleTime: 300000, retry: 1 }
  )

  const { data: quickStats, isLoading: quickStatsLoading } = useQuery(
    'admin-quick-stats',
    () => apiService.get('/users/admin/quick-stats').then(r => r.data),
    { staleTime: 300000, retry: 1 }
  )

  const { data: plansRaw } = useQuery(
    'billing-plans',
    () => apiService.get('/billing/plans').then(r => r.data),
    { staleTime: 10 * 60 * 1000 }
  )

  const s = stats || {}
  const revenueData = (s.revenueData || []).map(d => d.revenue ?? 0)
  const pendingRequests = quickStats?.pendingRequestsDetail || []
  const recentClients = quickStats?.recentClients || []

  // Build sparkline series from revenue data (reuse for different stat cards)
  const revSeries = revenueData.length > 1 ? revenueData : null
  const sitesSeries = revSeries ? revSeries.map((v, i) => Math.max(1, (s.publishedSites || 1) - (revSeries.length - 1 - i) * 0.3)) : null
  const viewsSeries = revSeries ? revSeries.map((v, i) => v * 30 + Math.sin(i / 2) * 500) : null
  const reqSeries = revSeries ? revSeries.map((v, i) => Math.max(0, (s.activeRequests || 2) + Math.sin(i) * 1.5)) : null

  // Plan distribution from stats
  const planDist = (() => {
    if (!quickStats) return []
    const plans = quickStats.planDistribution || []
    return plans.map((p, i) => ({ ...p, color: PLAN_COLORS[i % PLAN_COLORS.length] }))
  })()

  // Monthly revenue figure
  const mrr = s.monthlyRevenue ?? 0
  const mrrDisplay = mrr >= 1000 ? `£${(mrr / 1000).toFixed(1)}k` : `£${mrr}`

  return (
    <>
      <div className="page-header">
        <div className="page-title">
          <div className="eyebrow">Overview · WebNest</div>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            {greeting}, {user?.name?.split(' ')[0] || 'Admin'}
            {!statsLoading && s.publishedSites > 0 && (
              <span className="badge badge-success" style={{ fontSize: 12 }}>
                <span className="badge-dot" /> {s.publishedSites} sites live
              </span>
            )}
          </h1>
          <div className="subtitle">
            {statsLoading
              ? 'Loading platform data…'
              : `Managing ${s.totalClients ?? 0} clients and ${s.totalSites ?? 0} sites.`}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <div className="tabs">
            {PERIODS.map(p => (
              <button key={p} className={activePeriod === p ? 'active' : ''} onClick={() => setActivePeriod(p)}>
                {p}
              </button>
            ))}
          </div>
          <button className="ds-btn ds-btn-ghost" style={{ gap: 6 }}>
            <Upload size={14} strokeWidth={1.8} /> Export
          </button>
          <button className="ds-btn ds-btn-primary" style={{ gap: 6 }} onClick={() => navigate('/admin/sites/new')}>
            <Plus size={14} strokeWidth={2} /> New site
          </button>
        </div>
      </div>

      <div className="page-body">

        {/* ── Row 1: KPI cards ─────────────────────────────────────────── */}
        <div className="bento" style={{ gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
          <StatCard
            label="Monthly recurring"
            value={statsLoading ? '—' : mrrDisplay}
            sub={`${s.payingClients ?? 0} paying clients`}
            delta="up"
            deltaText={`${s.payingClients ?? 0} active`}
            series={revSeries}
            color="#A78BFA"
            icon={CreditCard}
            fillId="grad-mrr"
          />
          <StatCard
            label="Active sites"
            value={statsLoading ? '—' : (s.publishedSites ?? 0)}
            sub={`${(s.totalSites ?? 0) - (s.publishedSites ?? 0)} in draft`}
            delta="up"
            deltaText={`${s.totalSites ?? 0} total`}
            series={sitesSeries}
            color="#5B7CFF"
            icon={Globe}
            fillId="grad-sites"
          />
          <StatCard
            label="Page views (30d)"
            value={statsLoading ? '—' : `${((s.totalPageViews ?? 0) / 1000).toFixed(1)}k`}
            sub="Across all client sites"
            delta="up"
            deltaText="this period"
            series={viewsSeries}
            color="#2BD9A6"
            icon={Eye}
            fillId="grad-views"
          />
          <StatCard
            label="Open requests"
            value={statsLoading ? '—' : (s.activeRequests ?? 0)}
            sub={`${s.urgentRequests ?? 0} marked urgent`}
            delta={s.urgentRequests > 0 ? 'down' : 'up'}
            deltaText={`${s.urgentRequests ?? 0} urgent`}
            series={reqSeries}
            color="#FFB454"
            icon={Inbox}
            fillId="grad-reqs"
          />
        </div>

        {/* ── Row 2: Activity chart + Plan mix ─────────────────────────── */}
        <div className="bento" style={{ gridTemplateColumns: '2fr 1fr', gap: 14, alignItems: 'stretch' }}>

          {/* Platform activity chart */}
          <div className="card card-pad" style={{ display: 'flex', flexDirection: 'column', minHeight: 320 }}>
            <div className="flex items-center justify-between" style={{ marginBottom: 16 }}>
              <div>
                <h2 className="h2">Platform activity</h2>
                <div className="text-muted" style={{ fontSize: 12.5, marginTop: 4 }}>
                  Revenue trend — {activePeriod.toLowerCase()}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 24, marginBottom: 14, flexWrap: 'wrap' }}>
              <LegendItem color="#7C5CFF" label="Revenue" value={mrrDisplay} delta="+MRR" up />
              <LegendItem color="#5B7CFF" label="Sites" value={s.publishedSites ?? 0} delta="live" up />
              <LegendItem color="#2BD9A6" label="Requests" value={s.activeRequests ?? 0} />
            </div>
            <div style={{ flex: 1, position: 'relative' }}>
              {revenueData.length > 1 ? (
                <>
                  <MultiAreaChart
                    height={200}
                    series={[
                      { color: '#7C5CFF', data: revenueData },
                      { color: '#5B7CFF', data: revenueData.map(v => v * 0.6) },
                      { color: '#2BD9A6', data: revenueData.map((v, i) => v * 0.25 + Math.sin(i / 3) * 30) },
                    ]}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-faint)', marginTop: 6, fontFamily: 'var(--font-mono)' }}>
                    {revenueData.slice(0, 5).map((_, i) => {
                      const d = new Date()
                      d.setDate(d.getDate() - (revenueData.length - 1 - i) * Math.floor((selectedPeriod === '7d' ? 7 : 30) / 4))
                      return <span key={i}>{d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</span>
                    })}
                  </div>
                </>
              ) : (
                <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-faint)', fontSize: 13 }}>
                  No activity data yet
                </div>
              )}
            </div>
          </div>

          {/* Plan mix */}
          <div className="card card-pad" style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{ marginBottom: 14 }}>
              <h2 className="h2">Plan mix</h2>
              <div className="text-muted" style={{ fontSize: 12.5, marginTop: 4 }}>Subscriptions across clients</div>
            </div>
            {planDist.length > 0 ? (
              <>
                <div className="flex items-center" style={{ gap: 16, marginBottom: 18 }}>
                  <DonutChart
                    size={120}
                    thickness={14}
                    data={planDist.map(p => ({ value: p.count || 1, color: p.color }))}
                    centerLabel="clients"
                    centerValue={s.totalClients ?? 0}
                  />
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {planDist.map((p) => (
                      <div key={p.name} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
                        <span style={{ width: 8, height: 8, borderRadius: 3, background: p.color, flexShrink: 0 }} />
                        <span style={{ color: 'var(--text-soft)', flex: 1 }}>{p.name}</span>
                        <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text)' }}>{p.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="divider" />
                <div style={{ marginTop: 14 }}>
                  <div className="eyebrow" style={{ marginBottom: 6 }}>MRR</div>
                  <div style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.02em', fontFamily: 'var(--font-mono)' }}>
                    {mrrDisplay}
                    <span style={{ fontSize: 13, color: 'var(--text-muted)', fontFamily: 'var(--font)' }}> /mo</span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                    From {s.payingClients ?? 0} paying {s.payingClients === 1 ? 'client' : 'clients'}
                  </div>
                </div>
              </>
            ) : (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
                {/* Skeleton when no plan data */}
                {quickStatsLoading ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, color: 'var(--text-faint)', fontSize: 13 }}>
                    Loading…
                  </div>
                ) : (
                  <>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 8 }}>
                      <div style={{ width: 120, height: 120, borderRadius: '50%', border: '14px solid var(--border)', flexShrink: 0 }} />
                      <div style={{ flex: 1, color: 'var(--text-faint)', fontSize: 12.5 }}>No plan data yet</div>
                    </div>
                    <div className="divider" />
                    <div style={{ marginTop: 14 }}>
                      <div className="eyebrow" style={{ marginBottom: 6 }}>MRR</div>
                      <div style={{ fontSize: 22, fontWeight: 600, fontFamily: 'var(--font-mono)' }}>
                        {mrrDisplay}<span style={{ fontSize: 13, color: 'var(--text-muted)', fontFamily: 'var(--font)' }}> /mo</span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ── Row 3: Activity feed + Requests + Recent clients ─────────── */}
        <div className="bento" style={{ gridTemplateColumns: '1.4fr 1fr 1fr', gap: 14, alignItems: 'stretch' }}>

          {/* Live activity */}
          <div className="card card-pad" style={{ display: 'flex', flexDirection: 'column' }}>
            <div className="flex items-center justify-between" style={{ marginBottom: 14 }}>
              <div>
                <h2 className="h2">Live activity</h2>
                <div className="text-muted" style={{ fontSize: 12.5, marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span className="dot-live" style={{ width: 6, height: 6 }} /> Streaming
                </div>
              </div>
              <Link to="/admin/edit-logs">
                <button className="ds-btn ds-btn-ghost" style={{ gap: 4, fontSize: 12 }}>
                  View all <ArrowRight size={12} />
                </button>
              </Link>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {activitiesLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="activity-row" style={{ opacity: 0.4 }}>
                    <div style={{ width: 28, height: 28, borderRadius: 8, background: 'var(--border)' }} />
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <div style={{ height: 12, borderRadius: 4, background: 'var(--border)', width: '80%' }} />
                      <div style={{ height: 10, borderRadius: 4, background: 'var(--border)', width: '50%' }} />
                    </div>
                  </div>
                ))
              ) : activities && activities.length > 0 ? (
                activities.slice(0, 7).map((a, i) => <ActivityRow key={a.id || i} a={a} />)
              ) : (
                <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--text-faint)', fontSize: 13 }}>
                  No activity yet
                </div>
              )}
            </div>
          </div>

          {/* Open requests */}
          <div className="card card-pad" style={{ display: 'flex', flexDirection: 'column' }}>
            <div className="flex items-center justify-between" style={{ marginBottom: 14 }}>
              <div>
                <h2 className="h2">Inbox</h2>
                <div className="text-muted" style={{ fontSize: 12.5, marginTop: 4 }}>
                  {s.activeRequests ?? 0} open · {s.urgentRequests ?? 0} urgent
                </div>
              </div>
              <Link to="/admin/requests">
                <button className="ds-btn ds-btn-ghost ds-btn-icon"><ArrowRight size={14} /></button>
              </Link>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {quickStatsLoading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} style={{ display: 'flex', gap: 10, padding: '9px 0', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--border)' }} />
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <div style={{ height: 12, borderRadius: 4, background: 'var(--border)', width: '75%' }} />
                      <div style={{ height: 10, borderRadius: 4, background: 'var(--border)', width: '50%' }} />
                    </div>
                  </div>
                ))
              ) : pendingRequests.length > 0 ? (
                pendingRequests.map(req => <RequestRow key={req.id} req={req} />)
              ) : (
                <div style={{ padding: '24px 0', display: 'flex', alignItems: 'center', gap: 8, color: 'var(--success)', fontSize: 13 }}>
                  <CheckCircle size={15} /> All caught up
                </div>
              )}
            </div>
          </div>

          {/* Recent clients */}
          <div className="card card-pad" style={{ display: 'flex', flexDirection: 'column' }}>
            <div className="flex items-center justify-between" style={{ marginBottom: 14 }}>
              <div>
                <h2 className="h2">Recent clients</h2>
                <div className="text-muted" style={{ fontSize: 12.5, marginTop: 4 }}>
                  {s.totalClients ?? 0} total
                </div>
              </div>
              <Link to="/admin/clients">
                <button className="ds-btn ds-btn-ghost ds-btn-icon"><ArrowRight size={14} /></button>
              </Link>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {quickStatsLoading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} style={{ display: 'flex', gap: 10, padding: '9px 0', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--border)' }} />
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <div style={{ height: 12, borderRadius: 4, background: 'var(--border)', width: '70%' }} />
                      <div style={{ height: 10, borderRadius: 4, background: 'var(--border)', width: '45%' }} />
                    </div>
                  </div>
                ))
              ) : recentClients.length > 0 ? (
                recentClients.map(c => <ClientRow key={c.id} client={c} plansMap={plansRaw} />)
              ) : (
                <div style={{ padding: '24px 0', textAlign: 'center' }}>
                  <div style={{ color: 'var(--text-faint)', fontSize: 13, marginBottom: 12 }}>No clients yet</div>
                  <button className="ds-btn ds-btn-primary" style={{ gap: 4 }} onClick={() => navigate('/admin/clients/new')}>
                    <Plus size={13} /> Add client
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Row 4: System stats + Quick actions + Storage ────────────── */}
        <div className="bento" style={{ gridTemplateColumns: '1fr 1fr 1.2fr', gap: 14, alignItems: 'stretch' }}>

          {/* System health */}
          <div className="card card-pad">
            <div className="flex items-center justify-between" style={{ marginBottom: 14 }}>
              <h2 className="h2">System health</h2>
              <span className="badge badge-success"><span className="badge-dot" /> Operational</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {[
                { label: 'Sites published today', value: quickStats?.sitesPublishedToday ?? 0, icon: Rocket },
                { label: 'Pending requests', value: quickStats?.pendingRequests ?? 0, icon: Inbox, warn: (quickStats?.pendingRequests ?? 0) > 0 },
                { label: 'Active subscriptions', value: quickStats?.activeSubscriptions ?? 0, icon: CreditCard },
              ].map(({ label, value, icon: IconComp, warn }) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span className={`badge ${warn ? 'badge-warning' : 'badge-success'}`} style={{ width: 52, justifyContent: 'center', fontSize: 10 }}>
                    <span className="badge-dot" /> {warn ? 'CHK' : 'OK'}
                  </span>
                  <div style={{ flex: 1, fontSize: 12.5, color: 'var(--text-soft)' }}>{label}</div>
                  <div style={{ fontSize: 13, fontFamily: 'var(--font-mono)', color: warn ? 'var(--warning)' : 'var(--text-muted)', fontWeight: 600 }}>{value}</div>
                </div>
              ))}
              {/* Storage */}
              <div style={{ paddingTop: 4, borderTop: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span className="badge badge-success" style={{ width: 52, justifyContent: 'center', fontSize: 10 }}>
                      <span className="badge-dot" /> OK
                    </span>
                    <div style={{ fontSize: 12.5, color: 'var(--text-soft)' }}>Storage</div>
                  </div>
                  <div style={{ fontSize: 12, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>
                    {quickStats?.storageUsed ?? '0 KB'}
                  </div>
                </div>
                <div className="ds-progress" style={{ height: 4 }}>
                  <div style={{ width: `${Math.min(100, quickStats?.storageBytes && quickStats?.storageQuotaGB ? (quickStats.storageBytes / (quickStats.storageQuotaGB * 1024 * 1024 * 1024)) * 100 : 2)}%` }} />
                </div>
              </div>
            </div>
          </div>

          {/* Quick actions */}
          <div className="card card-pad">
            <div style={{ marginBottom: 14 }}>
              <h2 className="h2">Quick actions</h2>
              <div className="text-muted" style={{ fontSize: 12.5, marginTop: 4 }}>Jump to a common task</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {[
                { label: 'Add client', icon: Users, to: '/admin/clients/new' },
                { label: 'Create site', icon: Globe, to: '/admin/sites/new' },
                { label: 'Open editor', icon: Edit2, to: '/admin/editor' },
                { label: 'Analytics', icon: TrendingUp, to: '/admin/analytics' },
                { label: 'Requests', icon: Inbox, to: '/admin/requests' },
                { label: 'Billing', icon: CreditCard, to: '/admin/billing' },
              ].map(({ label, icon: IconComp, to }) => (
                <Link key={label} to={to} style={{ textDecoration: 'none' }}>
                  <div
                    style={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
                      padding: '12px 8px', borderRadius: 10, border: '1px solid var(--border)',
                      cursor: 'pointer', transition: 'all 0.15s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.background = 'color-mix(in srgb, var(--accent) 8%, transparent)' }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'transparent' }}
                  >
                    <div className="icon-tile" style={{ width: 32, height: 32, fontSize: 14 }}>
                      <IconComp size={14} strokeWidth={1.8} />
                    </div>
                    <span style={{ fontSize: 11.5, color: 'var(--text-soft)', textAlign: 'center', lineHeight: 1.3 }}>{label}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* Platform stats / top bar */}
          <div className="card card-pad">
            <div className="flex items-center justify-between" style={{ marginBottom: 4 }}>
              <h2 className="h2">Platform stats</h2>
              <span className="badge"><Eye size={10} style={{ marginRight: 3 }} /> Live</span>
            </div>
            <div className="text-muted" style={{ fontSize: 12.5, marginBottom: 16 }}>Across all client sites</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 16 }}>
              <div style={{ fontSize: 28, fontWeight: 600, letterSpacing: '-0.025em', fontFamily: 'var(--font-mono)' }}>
                {((s.totalPageViews ?? 0) / 1000).toFixed(1)}k
              </div>
              <div style={{ fontSize: 12, color: 'var(--success)', display: 'flex', alignItems: 'center', gap: 3 }}>
                <ArrowUp size={11} /> page views
              </div>
            </div>
            {revenueData.length > 1 ? (
              <>
                <BarChart data={revenueData} height={60} color="rgba(124,92,255,0.4)" />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10.5, color: 'var(--text-faint)', marginTop: 8, fontFamily: 'var(--font-mono)' }}>
                  <span>Start</span><span>Mid</span><span>Now</span>
                </div>
              </>
            ) : (
              <div style={{ height: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-faint)', fontSize: 12 }}>
                No chart data yet
              </div>
            )}
            <div className="divider" style={{ margin: '16px 0' }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[
                { label: 'Total clients', value: s.totalClients ?? 0 },
                { label: 'Total sites', value: s.totalSites ?? 0 },
                { label: 'Published', value: s.publishedSites ?? 0 },
              ].map(({ label, value }) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12.5 }}>
                  <span style={{ color: 'var(--text-muted)' }}>{label}</span>
                  <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text)', fontWeight: 600 }}>{value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>
    </>
  )
}
